/**
 * Optimiciti API Specification
 * Welcome to the Beta release of the Optimiciti API. This API allows registered users to make direct calls to our optimization solvers via a web request.  Use of the API requires acceptance of our terms of service.  Some useful links: - [Optimiciti Homepage](https://optimiciti.com) - [Optimiciti API Access Plans](https://optimiciti.com/pricing)
 *
 * The version of the OpenAPI document: 0.0.2
 * Contact: api@optimiciti.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 *
 */

import ApiClient from '../ApiClient';

/**
 * The TimeSettings model module.
 * @module model/TimeSettings
 * @version 0.0.2
 */
class TimeSettings {
    /**
     * Constructs a new <code>TimeSettings</code>.
     * This schema defines how to interpet the input time field settings. Optimiciti allows for input times to be specified as absolute numeric values (i.e 0, 12, 60, etc.) or as date/time values (i.e. 1/1/2024, 2:24pm).
     * @alias module:model/TimeSettings
     */
    constructor() { 
        
        TimeSettings.initialize(this);
    }

    /**
     * Initializes the fields of this object.
     * This method is used by the constructors of any subclasses, in order to implement multiple inheritance (mix-ins).
     * Only for internal use.
     */
    static initialize(obj) { 
        obj['timeUnit'] = 'integer';
        obj['dateUnit'] = 'integer';
    }

    /**
     * Constructs a <code>TimeSettings</code> from a plain JavaScript object, optionally creating a new instance.
     * Copies all relevant properties from <code>data</code> to <code>obj</code> if supplied or a new instance if not.
     * @param {Object} data The plain JavaScript object bearing properties of interest.
     * @param {module:model/TimeSettings} obj Optional instance to populate.
     * @return {module:model/TimeSettings} The populated <code>TimeSettings</code> instance.
     */
    static constructFromObject(data, obj) {
        if (data) {
            obj = obj || new TimeSettings();

            if (data.hasOwnProperty('timeUnit')) {
                obj['timeUnit'] = ApiClient.convertToType(data['timeUnit'], 'String');
            }
            if (data.hasOwnProperty('timeFormat')) {
                obj['timeFormat'] = ApiClient.convertToType(data['timeFormat'], 'String');
            }
            if (data.hasOwnProperty('multipleDays')) {
                obj['multipleDays'] = ApiClient.convertToType(data['multipleDays'], Object);
            }
            if (data.hasOwnProperty('dateUnit')) {
                obj['dateUnit'] = ApiClient.convertToType(data['dateUnit'], 'String');
            }
            if (data.hasOwnProperty('dateFormat')) {
                obj['dateFormat'] = ApiClient.convertToType(data['dateFormat'], 'String');
            }
        }
        return obj;
    }

    /**
     * Validates the JSON data with respect to <code>TimeSettings</code>.
     * @param {Object} data The plain JavaScript object bearing properties of interest.
     * @return {boolean} to indicate whether the JSON data is valid with respect to <code>TimeSettings</code>.
     */
    static validateJSON(data) {
        // ensure the json data is a string
        if (data['timeUnit'] && !(typeof data['timeUnit'] === 'string' || data['timeUnit'] instanceof String)) {
            throw new Error("Expected the field `timeUnit` to be a primitive type in the JSON string but got " + data['timeUnit']);
        }
        // ensure the json data is a string
        if (data['timeFormat'] && !(typeof data['timeFormat'] === 'string' || data['timeFormat'] instanceof String)) {
            throw new Error("Expected the field `timeFormat` to be a primitive type in the JSON string but got " + data['timeFormat']);
        }
        // ensure the json data is a string
        if (data['dateUnit'] && !(typeof data['dateUnit'] === 'string' || data['dateUnit'] instanceof String)) {
            throw new Error("Expected the field `dateUnit` to be a primitive type in the JSON string but got " + data['dateUnit']);
        }
        // ensure the json data is a string
        if (data['dateFormat'] && !(typeof data['dateFormat'] === 'string' || data['dateFormat'] instanceof String)) {
            throw new Error("Expected the field `dateFormat` to be a primitive type in the JSON string but got " + data['dateFormat']);
        }

        return true;
    }


}



/**
 * The time unit to use for optimization. This time unit will apply, as applicable, to location open and close windows, trip open and close windows, vehicle operating times, and travel durations. Integer (default) indicates that all times are in absolute time units from an arbitrarily defined time 0. Clock indicates that times will be interpreted as literal clock times and calendar dates.
 * @member {module:model/TimeSettings.TimeUnitEnum} timeUnit
 * @default 'integer'
 */
TimeSettings.prototype['timeUnit'] = 'integer';

/**
 * The format to expect all time fields in. This field is only relevant when timeUnit = \"clock\". Formattings should match the formats described by the python [pandas.Period.strftime class](https://pandas.pydata.org/docs/reference/api/pandas.Period.strftime.html). For example, if the format used matches 09:32pm, then '%I:%M%p'. If null is passed, then the [pandas.to_datetime function](https://pandas.pydata.org/docs/reference/api/pandas.to_datetime.html) will be used to detect the format.
 * @member {String} timeFormat
 */
TimeSettings.prototype['timeFormat'] = undefined;

/**
 * A boolean field indicating whether to allow the optimization time period to extend multiple days. This field is relevant only when timeUnit = \"datetime\".
 * @member {Object} multipleDays
 */
TimeSettings.prototype['multipleDays'] = undefined;

/**
 * The date unit to use for optimization. This field is only relevant when timeUnit = \"clock\" and multipleDays = true. If this field is integer, then days will be measured as absolute days from an arbitrarily defined day 0. Calendar indidates that dates will be interpreted as literal calendar dates.
 * @member {module:model/TimeSettings.DateUnitEnum} dateUnit
 * @default 'integer'
 */
TimeSettings.prototype['dateUnit'] = 'integer';

/**
 * The format to expect all dates fields in. This field is only relevant when dateUnit = \"calendar\", multipleDays = true, and timeUnit = \"clock\". Formattings should match the formats described by the python [pandas.Period.strftime class](https://pandas.pydata.org/docs/reference/api/pandas.Period.strftime.html). For example, if the format used matches 2024-12-31, then '%Y-%m-%d' should be passed for this parameter. If null is passed, then the [pandas.to_datetime function](https://pandas.pydata.org/docs/reference/api/pandas.to_datetime.html) will be used to detect the format.
 * @member {String} dateFormat
 */
TimeSettings.prototype['dateFormat'] = undefined;





/**
 * Allowed values for the <code>timeUnit</code> property.
 * @enum {String}
 * @readonly
 */
TimeSettings['TimeUnitEnum'] = {

    /**
     * value: "integer"
     * @const
     */
    "integer": "integer",

    /**
     * value: "clock"
     * @const
     */
    "clock": "clock"
};


/**
 * Allowed values for the <code>dateUnit</code> property.
 * @enum {String}
 * @readonly
 */
TimeSettings['DateUnitEnum'] = {

    /**
     * value: "integer"
     * @const
     */
    "integer": "integer",

    /**
     * value: "calendar"
     * @const
     */
    "calendar": "calendar"
};



export default TimeSettings;

